
import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import BrowseSidebar from './BrowseSidebar';
import BrowseGrid from './BrowseGrid';
import BrowseTagFilter from './BrowseTagFilter';
import { Story } from '@/utils/storiesData';
import SearchInput from '@/components/Search/SearchInput';
import { useNavigate } from 'react-router-dom';

interface BrowseMobileLayoutProps {
  filteredStories: Story[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  categoryCounts: Record<string, number>;
  tags?: string[];
  activeTag?: string | null;
  onTagChange?: (tag: string | null) => void;
  activeCategoryDescription?: string;
}

const BrowseMobileLayout: React.FC<BrowseMobileLayoutProps> = ({
  filteredStories,
  categories,
  activeCategory,
  onCategoryChange,
  categoryCounts,
  tags = [],
  activeTag = null,
  onTagChange = () => {},
  activeCategoryDescription = ''
}) => {
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <SearchInput
          query={searchQuery}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          onKeyDown={handleKeyDown}
          placeholder="Search for articles and films"
        />
      </div>
      
      <BrowseSidebar 
        categories={categories} 
        activeCategory={activeCategory} 
        onCategoryChange={onCategoryChange}
        categoryCounts={categoryCounts}
      />
      
      {tags.length > 0 && (
        <div className="mt-4 mb-6">
          <BrowseTagFilter
            tags={tags}
            activeTag={activeTag}
            onTagChange={onTagChange}
          />
        </div>
      )}
      
      <h2 className={cn("text-2xl font-chroma font-normal mb-2 capitalize", textColor)}>
        {activeCategory.replace(/-/g, ' ')}
      </h2>
      
      {activeCategoryDescription && (
        <p className={cn("text-base mb-6", mutedTextColor)}>
          {activeCategoryDescription}
        </p>
      )}
      
      {activeTag && (
        <div className="mb-6">
          <p className={cn("text-sm font-medium", textColor)}>
            Filtered by tag: <span className="font-semibold capitalize">{activeTag}</span>
          </p>
        </div>
      )}
      
      <BrowseGrid stories={filteredStories} />
    </div>
  );
};

export default BrowseMobileLayout;
